// ============================================
// JOB EDIT PAGE — Sadece durum yönetimi
// ============================================

import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import { ArrowLeft, Save, Building2, DollarSign, Calendar, Briefcase } from 'lucide-react';
import { PageHeader, StatusBadge } from '@/components';
import { adminApi } from '@/services/api';
import { formatDate, formatNumber, cn } from '@/utils';

export const Route = createFileRoute('/_authenticated/jobs/$id/edit')({
  component: JobEditPage,
});

function JobEditPage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [status, setStatus] = useState('DRAFT');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const { data: jobData, isLoading } = useQuery({
    queryKey: ['admin', 'jobs', id],
    queryFn: () => adminApi.getJob(id),
  });

  const job = (jobData as any)?.data || jobData;

  useEffect(() => {
    if (job) {
      setStatus(job.status || 'DRAFT');
    }
  }, [job]);

  const updateMutation = useMutation({
    mutationFn: (data: any) => adminApi.updateJob(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'jobs'] });
      navigate({ to: `/jobs/${id}` });
    },
    onError: (error: any) => {
      setErrors({ submit: error?.response?.data?.message || 'Bir hata oluştu' });
    },
  });

  const handleSubmit = () => {
    updateMutation.mutate({ status });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-maritime-600"></div>
      </div>
    );
  }

  const departmentLabels: Record<string, string> = {
    DECK: 'Güverte',
    ENGINE: 'Makine',
    ELECTRICAL: 'Elektrik',
    CATERING: 'Catering',
    OTHER: 'Diğer',
  };

  const statusOptions = [
    { value: 'DRAFT', label: 'Taslak' },
    { value: 'ACTIVE', label: 'Aktif' },
    { value: 'PAUSED', label: 'Duraklatıldı' },
    { value: 'CLOSED', label: 'Kapatıldı' },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="İlan Yönetimi"
        description={job?.title}
        actions={
          <Link
            to={`/jobs/${id}` as any}
            className="flex items-center gap-2 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-maritime hover:bg-gray-50"
          >
            <ArrowLeft className="h-4 w-4" />
            Geri
          </Link>
        }
      />

      {/* İlan bilgi özeti (read-only) */}
      <div className="bg-white rounded-maritime shadow-maritime p-6">
        <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">İlan Bilgileri</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-center gap-3">
            <Briefcase className="h-5 w-5 text-gray-400" />
            <div>
              <p className="text-xs text-gray-500">İlan Başlığı</p>
              <p className="font-medium text-gray-900">{job?.title || '-'}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Building2 className="h-5 w-5 text-gray-400" />
            <div>
              <p className="text-xs text-gray-500">Şirket</p>
              <p className="font-medium text-gray-900">{(job as any)?.company?.name || '-'}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <DollarSign className="h-5 w-5 text-gray-400" />
            <div>
              <p className="text-xs text-gray-500">Maaş</p>
              <p className="font-medium text-gray-900">
                {job?.salaryMin && job?.salaryMax
                  ? `${formatNumber(job.salaryMin)} - ${formatNumber(job.salaryMax)} ${job.salaryCurrency}`
                  : '-'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Calendar className="h-5 w-5 text-gray-400" />
            <div>
              <p className="text-xs text-gray-500">Departman</p>
              <p className="font-medium text-gray-900">{departmentLabels[job?.department] || job?.department || '-'}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Durum yönetimi */}
      <div className="bg-white rounded-maritime shadow-maritime p-6">
        <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">Durum Yönetimi</h3>
        
        <div className="flex items-center gap-3 mb-4">
          <span className="text-sm text-gray-500">Mevcut Durum:</span>
          <StatusBadge status={job?.status} dot />
        </div>

        <div className="max-w-sm">
          <label className="block text-sm font-medium text-gray-700 mb-1">Yeni Durum</label>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-maritime focus:outline-none focus:ring-2 focus:ring-maritime-500"
          >
            {statusOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        {errors.submit && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-maritime">
            <p className="text-sm text-red-600">{errors.submit}</p>
          </div>
        )}

        <div className="flex gap-3 mt-4">
          <button
            onClick={handleSubmit}
            disabled={updateMutation.isPending || status === job?.status}
            className="flex items-center gap-2 px-4 py-2 bg-maritime-600 text-white rounded-maritime hover:bg-maritime-700 disabled:opacity-50"
          >
            <Save className="h-4 w-4" />
            {updateMutation.isPending ? 'Kaydediliyor...' : 'Durumu Güncelle'}
          </button>
        </div>
      </div>
    </div>
  );
}
